import React, { useEffect, useMemo, useState, useId } from "react";

import { BaseCard, CardProps } from "../BaseCard";
import { pdfjs, Document, Page } from "react-pdf";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { useResizeDetector } from "react-resize-detector";
import { CircularProgress } from "../../../CircularProgress";
import { ControlBar, PaginationWrapper } from "./styles";
import { Dialog, DialogHeader, DialogContent } from "../../../dialog";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { Box, CircularProgress as MuiCircularProgress } from "@mui/material";
import { ArrowLeftIcon, ArrowRightIcon } from "../../../../../../icons";
import { useIntl } from "react-intl";

export type PaginationProps = CardProps & {
  learningCardId: string;
  isActive: boolean;
  title?: string;
  url?: string;
  paginatedPdfs: number;
  isCurrentCardComplete: boolean;
};

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export function Pagination({
  learningCardId,
  isActive,
  url,
  onCardActive,
  paginatedPdfs,
  isCurrentCardComplete,
}: PaginationProps) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [timeLeft, setTimeLeft] = useState(0);
  const [pdfDisplayWidth, setPdfDisplayWidth] = useState(0);
  const { formatMessage } = useIntl();
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageDimensions, setPageDimensions] = useState<{
    width?: number;
    height?: number;
  } | null>(null);

  const {
    width: containerWidth,
    height: containerHeight,
    ref: containerRef,
  } = useResizeDetector({
    refreshMode: "debounce",
    refreshRate: 100,
  });

  const handleNextPage = () => {
    if (pageNumber < numPages && timeLeft === 0) {
      setPageNumber(pageNumber + 1);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const [open, setOpen] = useState(false);

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const [maxProgress, setMaxProgress] = useState(1);

  useEffect(() => {
    const savedProgress = localStorage.getItem(
      `pdf_progress_${learningCardId}`
    );
    if (savedProgress) {
      const parsedProgress = Number(savedProgress);
      setMaxProgress(parsedProgress);
      setPageNumber(parsedProgress);
    }
  }, [learningCardId]);

  useEffect(() => {
    if (maxProgress > 1) {
      localStorage.setItem(
        `pdf_progress_${learningCardId}`,
        maxProgress.toString()
      );
    }
  }, [maxProgress, learningCardId]);

  const options = useMemo(
    () => ({
      cMapUrl: "cmaps/",
      cMapPacked: true,
      standardFontDataUrl: "standard_fonts/",
    }),
    []
  );

  useEffect(() => {
    if (pageNumber === numPages && numPages > 0 && isActive) {
      if (paginatedPdfs === 0 || maxProgress > numPages) {
        onCardActive?.();
      }
    }
  }, [
    pageNumber,
    numPages,
    isActive,
    onCardActive,
    paginatedPdfs,
    maxProgress,
  ]);

  useEffect(() => {
    if (paginatedPdfs > 0 && isActive && !isCurrentCardComplete) {
      if (pageNumber < maxProgress) {
        setTimeLeft(0);

        return;
      }

      setTimeLeft(paginatedPdfs);

      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setMaxProgress((prevMax) => Math.max(prevMax, pageNumber + 1));

            return 0;
          }

          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setTimeLeft(0);
    }
  }, [pageNumber, paginatedPdfs, isActive, maxProgress]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let active = true;

    async function updatePageDimensions() {
      if (!pdfDocument || !containerWidth || !containerHeight) {
        return;
      }

      try {
        const page = await pdfDocument.getPage(pageNumber);
        if (!active) return;

        const viewport = page.getViewport({ scale: 1 });
        const pageRatio = viewport.width / viewport.height;
        const containerRatio = containerWidth / containerHeight;

        let finalWidth = 0;

        if (pageRatio > containerRatio) {
          if (containerWidth < 600) {
            finalWidth = containerWidth;
          } else {
            finalWidth = Math.max(600, containerWidth - 400);
          }
          setPageDimensions({ width: finalWidth });
        } else {
          const finalHeight = containerHeight - 32;
          finalWidth = finalHeight * pageRatio;
          setPageDimensions({ height: finalHeight });
        }
        setPdfDisplayWidth(finalWidth);
      } catch (error) {
        console.error("Error getting page dimensions:", error);
      }
    }

    void updatePageDimensions();

    return () => {
      active = false;
    };
  }, [pdfDocument, pageNumber, containerWidth, containerHeight]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const isNextDisabled = pageNumber === numPages || timeLeft > 0;
  const progress =
    paginatedPdfs > 0
      ? ((paginatedPdfs - timeLeft) / paginatedPdfs) * 100
      : 100;

  return (
    <PaginationWrapper>
      <BaseCard isActive={true}>
        <div
          ref={containerRef}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Document
            onLoadSuccess={(pdf) => {
              setNumPages(pdf.numPages);
              setPdfDocument(pdf);

              const savedPage = localStorage.getItem(
                `pdf_progress_${learningCardId}`
              );
              if (savedPage) {
                const parsedPage = Number(savedPage);
                if (parsedPage > pdf.numPages) {
                  setPageNumber(pdf.numPages);
                }
              }
            }}
            loading={<CircularProgress />}
            file={url}
            options={options}
          >
            {pageDimensions && (
              <Page
                pageNumber={pageNumber}
                width={pageDimensions.width}
                height={pageDimensions.height}
                onClick={() => setOpen(true)}
                scale={1}
                loading={<CircularProgress />}
              />
            )}
          </Document>
        </div>
      </BaseCard>{" "}
      <ControlBar>
        <Box
          style={{
            width: pdfDisplayWidth || "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            onClick={handlePreviousPage}
            style={{
              cursor: pageNumber === 1 ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              color: "#809DC4",
              fontWeight: 600,
              userSelect: "none",
            }}
          >
            <ArrowLeftIcon
              $large
              style={{
                color: pageNumber === 1 ? "#afafaf" : "#809DC4",
                marginRight: "8px",
              }}
            />
            {formatMessage({
              id: "common.previous",
              defaultMessage: "Previous",
            })}
          </Box>
          <Box color={"#809DC4"} fontWeight={600}>
            {pageNumber} / {numPages}
          </Box>
          <Box
            onClick={handleNextPage}
            style={{
              cursor: isNextDisabled ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              fontWeight: 600,
            }}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: 600,
                gap: "8px",
              }}
            >
              {timeLeft > 0 && (
                <Box
                  style={{
                    position: "relative",
                    display: "inline-flex",
                    width: 24,
                    height: 24,
                  }}
                >
                  <MuiCircularProgress
                    variant="determinate"
                    value={100}
                    size={20}
                    thickness={6}
                    style={{
                      color: "#e0e0e0",
                      position: "absolute",
                      left: 0,
                      top: 0,
                    }}
                  />
                  <MuiCircularProgress
                    variant="determinate"
                    value={progress}
                    size={20}
                    thickness={6}
                    style={{
                      color: "#3f51b5",
                      position: "absolute",
                      left: 0,
                      top: 0,
                    }}
                  />
                </Box>
              )}
              <span
                style={{
                  color: isNextDisabled ? "#afafaf" : "#809DC4",
                  userSelect: "none",
                }}
              >
                {formatMessage({ id: "common.next", defaultMessage: "Next" })}
              </span>
              <ArrowRightIcon
                $large
                style={{
                  marginLeft: "8px",
                  color: isNextDisabled ? "#afafaf" : "#809DC4",
                }}
              />
            </Box>
          </Box>
        </Box>
      </ControlBar>
      <Dialog
        open={open}
        maxWidth="lg"
        aria-labelledby="PDF"
        fullScreen
        keepMounted
      >
        <DialogHeader id={useId()} onClose={() => setOpen(false)}>
          <span>PDF</span>
        </DialogHeader>
        <DialogContent>
          <Document
            file={url}
            options={options}
            onLoadSuccess={(pdf) => {
              setNumPages(pdf.numPages);
              setPdfDocument(pdf);
            }}
            loading={<CircularProgress />}
          >
            {pageDimensions && (
              <Page
                pageNumber={pageNumber}
                width={window.innerWidth - 40}
                scale={1}
                loading={<CircularProgress />}
              />
            )}
          </Document>
        </DialogContent>
      </Dialog>
    </PaginationWrapper>
  );
}
